import {BaseStage} from "./BaseStage";
import {loading, stopLoading} from "../../../helpers/loading";
import Axios from "axios";
import createElement from "../../../helpers/createElement";
import handleAjaxErrorV2 from "../../../helpers/handleAjaxErrorV2";
import {TotalPreviewComponent} from "../TotalPreviewComponent/TotalPreviewComponent";
import {getSingleInputByNameV3} from "../../../helpers/forms/formInputsHelpers";

class TicketTypeSelectStage extends BaseStage {
    /** @var {TotalPreviewComponent} */
    totalPreviewComponent;

    constructor(globalState) {
        super(globalState);

        this.domContainer = document.querySelector(`.stage.${this.getStageKey()}`)

        // Clear state
        if (this.clearStateAtStartup()) {
            this.globalState.setReserveTicketCounts([]);
            this.globalState.setGeneralAdmissionTicketCounts([]);
            this.globalState.setAddons([]);
            this.globalState.setDiscountCode('');
        }

        // Get route contents
        const getViewHtmlRoute = this.domContainer.dataset.getSeatTypesViewRoute;

        loading();

        const orderData = {
            order_id: this.globalState.orderId,
            orderType: this.globalState.selectedSeatType,
            event_date_id: this.globalState.selectedDate.id,
        }

        // Append selected seats if required
        if (this.globalState.selectedSeatType === 'reserved') {
            orderData.reserved_seats = this.globalState.selectedSeats;
        }

        Axios.post(
            getViewHtmlRoute,
            orderData
        )
            .then(response => {
                // Retrieve HTML from server
                const viewHtml = createElement(response.data.view);

                // Replace contents from server
                this.domContainer.innerHTML = viewHtml.innerHTML;

                // Run default components initializations
                this.initSharedComponents();

                this.initView();
            })
            .catch(error => {
                handleAjaxErrorV2(error)

                this.globalState.stageManager.previousStage();
                this.globalState.stageManager.clearStageInstance('seat-type-select');
            })
            .finally(stopLoading)
    }

    clearStateAtStartup() {
        return true;
    }

    /**
     * @return {PublicTicketsFormDefs.Stage}
     */
    getStageKey() {
        // noinspection JSValidateTypes
        return 'seat-type-select';
    }

    initView() {
        // Save order id from server
        this.globalState.setOrderId(
            getSingleInputByNameV3(this.domContainer, 'order_id')
        );


        // Init ticket count controls
        this.domContainer.querySelectorAll('.visitor_type_container .subtract-button').forEach(button => {
            button.addEventListener('click', () => {
                if (button.disabled === false) {
                    this.updateContainerCount(button.closest('.visitor_type_container'), -1)
                }
            })
        })

        this.domContainer.querySelectorAll('.visitor_type_container .add-button').forEach(button => {
            button.addEventListener('click', () => {
                if (button.disabled === false) {
                    this.updateContainerCount(button.closest('.visitor_type_container'), 1)
                }
            })
        })

        this.domContainer.querySelectorAll('.visitor_type_container input').forEach(input => {
            input.addEventListener('change', () => {
                this.cleanUpIntegerOnlyInput(input)
                // Check if it is a reserved seats section
                if (input.closest('.reserved-tickets')) {
                    this.handleReservedTicketInputCustomBehavior(input)
                }
                this.handleTicketCountChange();
            })
            input.addEventListener('keyup', () => {
                this.cleanUpIntegerOnlyInput(input)
            })
        })

        // Init addon controls
        //// Setup addon select cards
        this.domContainer.querySelectorAll('.addon-container').forEach(showContainer => {
            const addonInput = showContainer.querySelector(`input[type="checkbox"]`);

            addonInput.addEventListener('change', () => {
                this.handleAddonToggle(showContainer);
                this.handleAddonChange();
            })

            // Trigger show selection
            showContainer.addEventListener('click', (e) => {
                const clickedOnInput = e.target === addonInput;
                const clickedOnCountControl =
                    e.target.classList.contains('count-container') || // If we clicked on the count container
                    (e.target.closest('.count-container') !== null) // Or a children of it

                // If we clicked on neither the input or the count controls...
                if (clickedOnInput === false && clickedOnCountControl === false) {
                    // ... Toggle checkbox and dispatch event
                    addonInput.checked = !addonInput.checked;
                    addonInput.dispatchEvent(new InputEvent('change'));
                }
            })
        })

        //// Init addon count controls
        this.domContainer.querySelectorAll('.addon-container .subtract-button').forEach(button => {
            button.addEventListener('click', () => {
                this.updateContainerCount(button.closest('.count-container'), -1)
            })
        })

        this.domContainer.querySelectorAll('.addon-container .add-button').forEach(button => {
            button.addEventListener('click', () => {
                this.updateContainerCount(button.closest('.count-container'), 1)
            })
        })

        this.domContainer.querySelectorAll('.addon-container input[type="text"]').forEach(input => {
            input.addEventListener('change', () => {
                this.cleanUpIntegerOnlyInput(input)
                this.handleAddonChange();
            })
            input.addEventListener('keyup', () => {
                this.cleanUpIntegerOnlyInput(input)
            })
        })

        // Setup handlers to update totals on change
        this.domContainer.querySelectorAll('*[data-update-totals-on-change]').forEach(input => {
            input.addEventListener('change', () => {
                // We put this inside a set timeout to make sure it executes AFTER
                // all other handlers update the state
                setTimeout(() => {
                    this.updateTotals();
                }, 0)
            })
        })

        this.totalPreviewComponent = this.getTotalPreviewComponent()

        // Prefill data
        this.prefillData();

        // First render
        this.domContainer.querySelectorAll('.visitor_type_container input').forEach(input => {
            // Trigger change event so all clean up and derived
            // values (remaining tickets for example) are set.
            input.dispatchEvent(new Event('change'));
        })
        this.handleTicketCountChange();
        this.updateTotals();


        // Override default next stage button
        this.domContainer.querySelector('.next-stage-button')
            .addEventListener(
                'click', e => {
                    e.stopPropagation();
                    e.preventDefault();

                    // Check for cancellation insurance
                    let hasCancellationInsurance = false;
                    this.globalState.addons.forEach(addon => {
                        // Cancellation insure addon id
                        if (addon.key === 'cancellation-insurance') {
                            hasCancellationInsurance = true;
                        }
                    })

                    if (this.shouldDisplayTosWarning()) {
                        // Display TOS warning
                        Swal.fire({
                            title: '',
                            html: `
                            By clicking continue I agree to the terms and conditions linked below and understand that all purchases are final and non-refundable.
                            <br>
                            <br>
                            <div class="text-center">
                                <a href="https://thepromiseglenrose.com/tampc/" target="_blank">
                                    Terms and
                                    conditions.
                                </a>
                            </div>
                            `,
                            showCancelButton: true,
                            confirmButtonText: 'Continue',
                        }).then(result => {
                            if (result.isConfirmed) {
                                this.globalState.stageManager.nextStage();
                            }
                        })
                    } else {
                        // Go to next stage without displaying TOS warning
                        this.globalState.stageManager.nextStage();
                    }


                },
                {
                    capture: true,
                }
            )
    }

    getTotalPreviewComponent() {
        return new TotalPreviewComponent(
            this.domContainer.querySelector('.totals-preview-wrapper'),
            this.globalState,
        );
    }

    shouldDisplayTosWarning() {
        return true;
    }

    handleTicketCountChange() {
        if (this.globalState.selectedSeatType === 'general-admission') {
            this.saveGeneralAdmissionTicketCount();
            if (this.globalState.generalAdmissionTicketCounts.length > 0) {
                this.enableNextStageButton();
            } else {
                this.disableNextStageButton();
            }
        } else {
            this.saveReserveTicketCount();

            let reservedTicketTotalCount = 0;

            this.globalState.reserveTicketCounts.forEach(ticket => {
                reservedTicketTotalCount += ticket.count
            })

            // Only enable next step if total tickets
            // matches number of selected seats
            if (reservedTicketTotalCount === this.globalState.selectedSeats.length) {
                this.enableNextStageButton();
            } else {
                this.disableNextStageButton();
            }
        }
    }


    saveGeneralAdmissionTicketCount() {
        /** @var {PublicTicketsFormDefs.SectionTicketCount[]} */
        const generalAdmissionTicketCount = [];

        // Get all general admission inputs
        this.domContainer.querySelectorAll('.general-admission-tickets .seat-type-input').forEach(input => {
            this.retrieveGeneralAdmissionTicketCount(input, generalAdmissionTicketCount);
        })

        this.globalState.setGeneralAdmissionTicketCounts(generalAdmissionTicketCount);
    }

    /**
     * Retrieves and processes the general admission ticket count from the provided input element.
     *
     * @param {HTMLInputElement} input
     * @param {PublicTicketsFormDefs.SectionTicketCount[]} generalAdmissionTicketCount
     * @return {void}
     */
    retrieveGeneralAdmissionTicketCount(input, generalAdmissionTicketCount) {
        const count = parseInt(input.value);

        // Skip empty entries
        if (isNaN(count)) {
            return;
        }

        generalAdmissionTicketCount.push({
            count,
            visitorTypeId: input.dataset.visitorTypeId,
            groupId: input.dataset.groupId,
        })
    }

    saveReserveTicketCount() {
        /** @var {PublicTicketsFormDefs.SectionTicketCount[]} */
        const reservedTicketCounts = [];

        // Get all general admission inputs
        this.domContainer.querySelectorAll('.reserved-tickets .seat-type-input').forEach(input => {
            this.retrieveReservedTicketCount(input, reservedTicketCounts);
        })

        this.globalState.setReserveTicketCounts(reservedTicketCounts);
    }

    /**
     * Retrieves and processes the reserved ticket count from the provided input element.
     *
     * @param {HTMLInputElement} input
     * @param {PublicTicketsFormDefs.SectionTicketCount[]} reservedTicketCounts
     * @return {void}
     */
    retrieveReservedTicketCount(input, reservedTicketCounts) {
        const count = parseInt(input.value);

        // Skip empty entries
        if (isNaN(count)) {
            return;
        }

        // noinspection JSCheckFunctionSignatures
        reservedTicketCounts.push({
            count,
            visitorTypeId: input.dataset.visitorTypeId,
            groupId: input.dataset.groupId,
        })
    }

    handleAddonChange() {
        /** @var {PublicTicketsFormDefs.Addon[]} */
        const addons = [];

        // Get all general admission inputs
        this.domContainer.querySelectorAll('.addon-container input[type="checkbox"]:checked').forEach(input => {
            const addonId = input.value;
            const key = input.dataset.key;
            // Retrieve addon count if required
            if (input.dataset.withCount) {
                const count = parseInt(input.closest('.addon-container').querySelector('.addon-count-input').value);

                if (isNaN(count)) {
                    // Skip addon if count is not valid
                    return;
                }

                addons.push({
                    addon_id: addonId,
                    key,
                    count
                })
            } else {
                addons.push({
                    addon_id: addonId,
                    key,
                })
            }
        })

        this.globalState.setAddons(addons);
    }

    /**
     * @param {HTMLElement} container
     * @param {number} delta
     */
    updateContainerCount(container, delta) {
        const input = container.querySelector('input');

        let currentValue = parseInt(input.value);
        if (isNaN(currentValue)) {
            currentValue = 0;
        }

        const newValue = currentValue + delta;

        if (newValue <= 0) {
            input.value = ''
        } else {
            input.value = newValue.toFixed(0)
        }

        // Trigger change event on input
        input.dispatchEvent(new InputEvent('change'))
    }

    /**
     * @param {HTMLInputElement} input
     */
    cleanUpIntegerOnlyInput(input) {
        input.value = input.value.replace(/[^0-9]/g, '')
    }

    /**
     * @param {HTMLInputElement} input
     */
    handleReservedTicketInputCustomBehavior(input) {
        let inputValue = parseInt(input.value);

        if (isNaN(inputValue)) {
            inputValue = 0;
        }

        const parentSection = input.parentElement.closest('.seat-group-section');
        const requiredTickets = parseInt(parentSection.dataset.maxAvailableSeats);

        // First, make sure we are not going out of bounds in the section
        let sectionTotalTickets = 0;

        parentSection.querySelectorAll('.seat-type-input').forEach(input => {
            const ticketCount = parseInt(input.value);

            if (isNaN(ticketCount)) {
                return
            }

            sectionTotalTickets += ticketCount;
        })

        // Subtract from section total the current input, so we
        // can know what is the maximum allowed value remaining
        sectionTotalTickets -= inputValue;

        const maxRemaining = requiredTickets - sectionTotalTickets;

        // If out of bounds, cap input value
        if (inputValue > maxRemaining) {
            inputValue = maxRemaining
        }

        if (inputValue) {
            // Update input with new max value
            input.value = inputValue.toFixed(0);
        } else {
            // Or clear the input
            input.value = ''
        }

        // Add back input value now that it has been capped
        sectionTotalTickets += inputValue;

        // And update section header and buttons
        const remainingTicketsRequiredInSection = requiredTickets - sectionTotalTickets;
        parentSection.querySelector('.section-seats-remaining').innerHTML = remainingTicketsRequiredInSection.toFixed(0);

        if (remainingTicketsRequiredInSection < 1) {
            // Disable all add buttons
            parentSection.querySelectorAll('.add-button').forEach(button => {
                button.disabled = true;
            })
        } else {
            // Enable all add buttons
            parentSection.querySelectorAll('.add-button').forEach(button => {
                button.disabled = false;
            })
        }
    }


    /**
     * @param {HTMLElement} showContainer
     */
    handleAddonToggle(showContainer) {
        /** @var {HTMLInputElement} */
        const addonInput = showContainer.querySelector(`input`);

        // Toggle selected class
        showContainer.classList.toggle('selected', addonInput.checked);

        if (addonInput.dataset.withCount) {
            showContainer.querySelector('.count-control-container')
                .classList
                .toggle('d-none', addonInput.checked === false)
        }
    }

    updateTotals() {
        this.totalPreviewComponent.updateTotalsFromServer().then();
    }

    /**
     * Prefills server response HTML with our data in memory.
     * Public instance always requires the user to start from scratch
     * while admin instance allows him to preserve his changes.
     */
    prefillData() {
        // Do nothing, only admin instance preserves state
    }
}

export {TicketTypeSelectStage};
